import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Stack,
  toast,
} from "@altimateai/lego";
import { BetterPopover } from "../popover";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { savedFilterSchema, Filter, FilterApplicableFor, FilterType, SavedFilter } from "./types";
import { FilterTag } from ".";
import { flattenFilters } from "./utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { createFilter } from "@/helpers/apis";
import { LoadingButton } from "@altimateai/lego";
import { IconDeviceFloppy } from "@tabler/icons-react";

interface Props {
  applicableFor: FilterApplicableFor;
  filters: Record<string, Filter>;
}
const SaveFilters = ({ applicableFor, filters: availableFilters }: Props) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [filters, setFilters] = useState(availableFilters);

  useEffect(() => {
    setFilters(availableFilters);
  }, [availableFilters]);

  const flatFilters = useMemo(() => flattenFilters(filters), [filters]);

  const form = useForm<Omit<SavedFilter, "id">>({
    resolver: zodResolver(savedFilterSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      data: {},
      applicable_for: applicableFor,
      type: FilterType.FILTER,
    },
  });

  const {
    setValue,
    formState: { errors, isValid },
    register,
  } = form;

  useEffect(() => {
    const newData = Object.entries(filters).reduce((acc: Record<string, unknown>, [, filter]) => {
      if (filter.filterData) {
        Object.assign(acc, filter.filterData);
        return acc;
      }
      if (!filter.searchKey) {
        return acc;
      }
      const filterStr = filter.filterStr;

      // If filter value is string
      // Or if it is array, should contain atleast 1 value
      if (filterStr && (!Array.isArray(filterStr) || filterStr.length)) {
        acc[filter.searchKey] = {
          values: filterStr,
          condition: filter.condition || "equals",
        };
      }
      return acc;
    }, {});
    setValue("data", newData, { shouldValidate: true });
  }, [filters, setValue]);

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: createFilter,
    onSuccess: resp => {
      if (resp?.preset_id) {
        toast({ description: "Filter successfully saved!" });
        const event = new CustomEvent("showFilter", {
          detail: resp?.preset_id,
        });
        document.dispatchEvent(event);
        ref.current?.click();
      }
    },
    onError: error => {
      toast({
        title: "Failed to save filter",
        description: error?.toString() || "Failed to save filter",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: Omit<SavedFilter, "id">) => {
    mutate(data);
  };

  const handleFilterDelete = (removedFilter: string, filter: Filter) => {
    setFilters(prevFilters => {
      return Object.entries(prevFilters).reduce((acc: Record<string, Filter>, [key, value]) => {
        let clone: Filter | undefined = { ...value };
        if (value.label === filter.label) {
          // If filterStr is array, remove the removedFilter from the array
          if (Array.isArray(value.filterStr)) {
            clone.filterStr = value.filterStr.filter(filterStr => filterStr !== removedFilter);
            // If filterStr is string or custom data, remove the filter
          } else if (value.filterData || typeof value.filterStr === "string") {
            clone = undefined;
          }
        }
        if (clone) {
          acc[key] = clone;
        }
        return acc;
      }, {});
    });
  };

  return (
    <BetterPopover
      id="save-filter-popover"
      trigger={
        <Button variant="secondary" ref={ref}>
          <IconDeviceFloppy />
          Save filter
        </Button>
      }
      render={({ close }) => (
        <Card className="al-m-0" style={{ width: 480 }}>
          <CardHeader className="al-px-3 al-pt-2 al-pb-0">
            <CardTitle>Save filter</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="mb-3">
                <Label>Filter Name</Label>
                <Input
                  {...register("name")}
                  placeholder="Name of the filter"
                  className={errors.name ? "border-destructive" : ""}
                />
                {errors.name ? (
                  <div className="text-sm text-destructive mt-1">{errors.name.message}</div>
                ) : null}
              </div>

              <Stack className="flex-wrap">
                {flatFilters.map((filter, index) => {
                  const key = filter.filterStr
                    ? Array.isArray(filter.filterStr)
                      ? filter.filterStr.join()
                      : filter.filterStr
                    : index;
                  return (
                    <FilterTag
                      key={key}
                      searchKey={undefined} // set as undefined to not remove from url params in save filters
                      label={filter.label}
                      filterStr={
                        Array.isArray(filter.filterStr)
                          ? filter.filterStr.join(", ")
                          : String(filter.filterStr)
                      }
                      onClose={(removedFilter: string) => {
                        handleFilterDelete(removedFilter, filter);
                      }}
                      bgColor={filter.bgColor || "grey"}
                    />
                  );
                })}
              </Stack>

              {errors.data ? (
                <div className="text-sm text-destructive mt-1">{errors.data.message as string}</div>
              ) : null}

              <div className="mt-3">
                <LoadingButton loading={isLoading} disabled={!isValid}>
                  Save
                </LoadingButton>
                <Button variant="link" onClick={close}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    />
  );
};

export default SaveFilters;
